//#region Import
import React, { useEffect, useState, useCallback } from 'react';
import { useSelector } from 'react-redux';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Card,
  CardContent,
  Chip,
  Fade,
  Slide,
} from '@mui/material';
import {
  SystemUpdateAlt,
  PhoneAndroid,
  PhoneIphone,
} from '@mui/icons-material';
import { Capacitor } from '@capacitor/core';
import Commons from '../../Services/Common/Common';
import Global from '../../Services/Common/Global';
import { apiRequest } from '../../Services/Common/ApiRequest';
//#endregion

function UpdateDialog() {
  const [latestVersion, setLatestVersion] = useState('');

  // 로그인된 경우에만 버전 조회 (세션 없을 때 GetVersion 호출 시 401 방지)
  const token = useSelector((state) => state?.BaseInfo?.userInfo?.token ?? null);

  // 스토어 URL (고정값)
  const iosStoreUrl = 'https://apps.apple.com/app/id6752674705';
  const androidStoreUrl =
    'https://play.google.com/store/apps/details?id=com.SafeOn.app';

  // 로컬스토리지에서 현재 버전 가져오기
  const currentVersion = Commons.Storages('get', 'version')?.version;

  // 재시도 로직이 포함된 버전 조회 함수
  const SearchLogic1 = useCallback(async (retryCount = 0, maxRetries = 3) => {
    try {
      // 네트워크 상태 확인
      if (!navigator.onLine) {
        throw new Error('Network offline');
      }

      const result = await apiRequest(
        'post',
        Global.urls.Utility.getVersion,
        {}
      );

      let data = result.data;

      if (data.length > 0) {
        if (data[0].type === 'Error') {
          throw new Error(data[0].message);
        } else {
          setLatestVersion(data.find((f) => f.type === 'MOBILE')?.version);
        }
      }
    } catch (e) {
      // Network Error이고 재시도 가능한 경우
      const isNetworkError =
        e?.code === 'ERR_NETWORK' ||
        e?.message === 'Network Error' ||
        e?.message === 'Network offline' ||
        e?.name === 'AxiosError';

      if (isNetworkError && retryCount < maxRetries) {
        // Exponential backoff: 500ms, 1000ms, 2000ms
        const delay = Math.min(500 * Math.pow(2, retryCount), 2000);

        setTimeout(() => {
          SearchLogic1(retryCount + 1, maxRetries);
        }, delay);
      } else {
        // 최대 재시도 횟수 초과 또는 네트워크 에러가 아닌 경우
        // 업데이트 다이얼로그는 표시하지 않고 조용히 실패
        console.warn(
          '버전 조회 실패 (최대 재시도 횟수 초과 또는 네트워크 오류):',
          e?.message || JSON.stringify(e)
        );
      }
    }
  }, []);

  // DB에서 최신 버전 조회
  useEffect(() => {
    if (!token) return;

    // 앱 초기 로딩 시 네트워크가 준비될 시간을 주기 위해 약간의 지연 추가
    const timer = setTimeout(() => {
      SearchLogic1();
    }, 500);

    return () => {
      clearTimeout(timer);
    };
  }, [token, SearchLogic1]);

  // 버전 비교 (간단한 문자열 비교)
  const ShouldUpdate = () => {
    if (!latestVersion) return false;
    if (!currentVersion) return true;

    // v 제거하고 숫자만 추출
    const current = currentVersion.replace(/^v/i, '').trim();
    const latest = latestVersion.replace(/^v/i, '').trim();

    return latest > current;
  };

  // 플랫폼 감지 (간단하게)
  const GetPlatform = () => {
    try {
      if (Capacitor?.getPlatform) {
        return Capacitor.getPlatform();
      }
    } catch (_) { }
    return navigator.userAgent.includes('iPhone') ||
      navigator.userAgent.includes('iPad')
      ? 'ios'
      : 'android';
  };

  const HandleUpdate = () => {
    const platform = GetPlatform();
    const url = platform === 'ios' ? iosStoreUrl : androidStoreUrl;
    window.location.href = url;
  };

  // 업데이트가 필요하지 않으면 렌더링하지 않음
  if (!ShouldUpdate()) return null;

  const platform = GetPlatform();
  const platformIcon = platform === 'ios' ? <PhoneIphone /> : <PhoneAndroid />;

  return (
    <Dialog
      open={true}
      fullWidth
      maxWidth="sm"
      PaperProps={{
        sx: {
          borderRadius: 3,
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          color: 'white',
          overflow: 'hidden',
        },
      }}
      TransitionComponent={Slide}
      TransitionProps={{ direction: 'up' }}
    >
      {/* 헤더 */}
      <Box
        sx={{
          position: 'relative',
          background: 'rgba(255,255,255,0.1)',
          backdropFilter: 'blur(10px)',
          borderBottom: '1px solid rgba(255,255,255,0.2)',
        }}
      >
        <DialogTitle
          sx={{
            textAlign: 'center',
            py: 3,
            position: 'relative',
            '&::before': {
              content: '""',
              position: 'absolute',
              top: 0,
              left: '50%',
              transform: 'translateX(-50%)',
              width: 60,
              height: 4,
              background: 'linear-gradient(90deg, #ff6b6b, #4ecdc4)',
              borderRadius: 2,
            },
          }}
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 1,
            }}
          >
            <SystemUpdateAlt sx={{ fontSize: 28, color: '#4ecdc4' }} />
            <Typography variant="h5" fontWeight="bold">
              업데이트 알림
            </Typography>
          </Box>
        </DialogTitle>
      </Box>

      {/* 콘텐츠 */}
      <DialogContent sx={{ py: 4, px: 3 }}>
        <Fade in timeout={800}>
          <Card
            sx={{
              background: 'rgba(255,255,255,0.95)',
              backdropFilter: 'blur(10px)',
              borderRadius: 2,
              boxShadow: '0 8px 32px rgba(0,0,0,0.1)',
              border: '1px solid rgba(255,255,255,0.2)',
              color: '#333',
            }}
          >
            <CardContent sx={{ p: 3 }}>
              <Box sx={{ textAlign: 'center', mb: 3 }}>
                <Typography
                  variant="h6"
                  fontWeight="600"
                  color="#2c3e50"
                  gutterBottom
                >
                  새로운 버전이 출시되었습니다! 🎉
                </Typography>
                <Typography variant="body1" color="#7f8c8d" sx={{ mb: 2 }}>
                  더 나은 성능과 새로운 기능을 경험해보세요
                </Typography>
              </Box>

              {/* 버전 정보 */}
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  mb: 3,
                  p: 2,
                  background:
                    'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)',
                  borderRadius: 2,
                  border: '1px solid rgba(0,0,0,0.05)',
                }}
              >
                <Box sx={{ textAlign: 'center', flex: 1 }}>
                  <Typography variant="caption" color="#7f8c8d" display="block">
                    현재 버전
                  </Typography>
                  <Chip
                    label={currentVersion || '-'}
                    size="small"
                    sx={{
                      background: '#e74c3c',
                      color: 'white',
                      fontWeight: 'bold',
                      mt: 0.5,
                    }}
                  />
                </Box>

                <Box
                  sx={{
                    width: 40,
                    height: 2,
                    background: 'linear-gradient(90deg, #e74c3c, #27ae60)',
                    borderRadius: 1,
                    mx: 2,
                  }}
                />

                <Box sx={{ textAlign: 'center', flex: 1 }}>
                  <Typography variant="caption" color="#7f8c8d" display="block">
                    최신 버전
                  </Typography>
                  <Chip
                    label={latestVersion || '-'}
                    size="small"
                    sx={{
                      background: '#27ae60',
                      color: 'white',
                      fontWeight: 'bold',
                      mt: 0.5,
                    }}
                  />
                </Box>
              </Box>

              {/* 플랫폼 정보 */}
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 1,
                  p: 1,
                  background: 'rgba(52, 152, 219, 0.1)',
                  borderRadius: 1,
                  border: '1px solid rgba(52, 152, 219, 0.2)',
                }}
              >
                {platformIcon}
                <Typography variant="body2" color="#3498db" fontWeight="500">
                  {platform === 'ios' ? 'iOS App Store' : 'Google Play Store'}
                  에서 업데이트
                </Typography>
              </Box>
            </CardContent>
          </Card>
        </Fade>
      </DialogContent>

      {/* 액션 버튼 */}
      <DialogActions
        sx={{
          p: 3,
          background: 'rgba(255,255,255,0.1)',
          backdropFilter: 'blur(10px)',
          borderTop: '1px solid rgba(255,255,255,0.2)',
        }}
      >
        <Button
          onClick={HandleUpdate}
          variant="contained"
          size="large"
          fullWidth
          sx={{
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            borderRadius: 2,
            py: 1.5,
            fontSize: '1.1rem',
            fontWeight: 'bold',
            textTransform: 'none',
            boxShadow: '0 4px 20px rgba(102, 126, 234, 0.4)',
            '&:hover': {
              background: 'linear-gradient(135deg, #5a6fd8 0%, #6a4190 100%)',
              boxShadow: '0 6px 25px rgba(102, 126, 234, 0.6)',
              transform: 'translateY(-2px)',
            },
            transition: 'all 0.3s ease',
          }}
        >
          <SystemUpdateAlt sx={{ mr: 1 }} />
          지금 업데이트하기
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default UpdateDialog;
